/**
 * ColumnPicker — dropdown to toggle visibility + reorder invoice-list columns.
 *
 * Phase 13 Cluster A, Feature B. Uses click-outside + escape to close. No
 * drag-and-drop; ordering is done via up/down buttons to keep the diff
 * dependency-free.
 */
import { useEffect, useRef, useState } from 'react';
import {
  type ColumnPrefs,
  moveColumn,
  resetColumnPrefs,
  toggleColumn,
} from '../../hooks/useInvoiceColumnPrefs';

export interface ColumnPickerOption {
  key: string;
  label: string;
}

interface ColumnPickerProps {
  options: readonly ColumnPickerOption[];
  prefs: ColumnPrefs;
  onChange: (next: ColumnPrefs) => void;
}

export function ColumnPicker({ options, prefs, onChange }: ColumnPickerProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const labelByKey = new Map(options.map((o) => [o.key, o.label]));
  const orderedOptions = prefs.order
    .map((key) => ({ key, label: labelByKey.get(key) ?? key }))
    .filter((o) => labelByKey.has(o.key));

  const visibleCount = orderedOptions.filter((o) => !prefs.hidden.includes(o.key)).length;

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="h-8 px-2.5 inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/10 transition-colors"
        title="Spalten konfigurieren"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
        </svg>
        <span className="hidden lg:inline">Spalten</span>
        <span className="text-white/40 text-xs">({visibleCount})</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-50 w-64 bg-black/95 backdrop-blur-xl border border-white/10 rounded-lg shadow-xl py-2"
        >
          <div className="px-3 py-1 text-[10px] font-medium text-white/40 uppercase tracking-wider">
            Spalten
          </div>
          <ul className="space-y-0.5 max-h-80 overflow-y-auto">
            {orderedOptions.map((opt, idx) => {
              const visible = !prefs.hidden.includes(opt.key);
              const isFirst = idx === 0;
              const isLast = idx === orderedOptions.length - 1;
              const isLastVisible =
                visible &&
                orderedOptions.filter((o) => !prefs.hidden.includes(o.key)).length === 1;
              return (
                <li
                  key={opt.key}
                  className="px-3 py-1.5 flex items-center gap-2 hover:bg-white/5"
                >
                  <label
                    className={`flex items-center gap-2 flex-1 ${isLastVisible ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    title={isLastVisible ? 'Mindestens eine Spalte muss sichtbar bleiben' : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={visible}
                      disabled={isLastVisible}
                      onChange={() => onChange(toggleColumn(prefs, opt.key))}
                      className="w-4 h-4 rounded border-white/20 bg-white/5 text-white accent-white/80 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                    />
                    <span className={`text-sm ${visible ? 'text-white' : 'text-white/50'}`}>
                      {opt.label}
                    </span>
                  </label>
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => onChange(moveColumn(prefs, opt.key, 'up'))}
                      disabled={isFirst}
                      aria-label={`${opt.label} nach oben`}
                      className="p-1 rounded text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => onChange(moveColumn(prefs, opt.key, 'down'))}
                      disabled={isLast}
                      aria-label={`${opt.label} nach unten`}
                      className="p-1 rounded text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="mt-1 pt-1 border-t border-white/10 px-3 py-1">
            <button
              type="button"
              onClick={() => onChange(resetColumnPrefs(options.map((o) => o.key)))}
              className="text-xs text-white/50 hover:text-white transition-colors"
            >
              Zurücksetzen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
